import React, { useState, useEffect, useMemo } from "react";
import { CheckCircle, XCircle, AlertCircle } from "lucide-react";
import StaffAttendanceChart from "./charts/StaffAttendanceChart";
import TaskStatusPieChart from "./TaskStatusPieChart";
import TimeSheetDisplay from "./TimeSheet";
import { format } from "date-fns";
import { CalendarDays, Check, X } from "lucide-react";

const formatDisplayDate = (date) => format(new Date(date), "dd/MM/yyyy");

const AttendanceTab = ({
  sendNotification,
  adminId,
  monthlySummary,
  selectedMonth,
  selectedYear,
  handleChange,
  months,
  taskCounts,
  staffTasks,
  staffData,
  leaves,
  setLeaves,
  attendanceAPI,
  dailyAttendanceData,
  formatDate,
}) => {
  const [performance, setPerformance] = useState(null);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [attendanceFilter, setAttendanceFilter] = useState("all");
  const staffId = staffData?.data?.staffId;

  useEffect(() => {
    const fetchPerformance = async () => {
      if (selectedMonth !== undefined && selectedYear !== undefined) {
        try {
          const data = await attendanceAPI.getAllStaffWorkfolioSummary({
            month: selectedMonth === 0 ? null : selectedMonth,
            year: selectedYear,
          });
          setPerformance(data);
        } catch (error) {
          console.error(
            "Error fetching performance data in AttendanceTab:",
            error
          );
          setPerformance(null);
        }
      }
    };
    fetchPerformance();
  }, [selectedMonth, selectedYear, attendanceAPI, staffData?.data?.staffId]);

  // Filter data based on attendanceFilter
  const getFilteredData = (data) => {
    if (attendanceFilter === "all" || !data) return data;
    
    const now = new Date();
    let startDate;
    
    switch (attendanceFilter) {
      case "monthly":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "quarterly":
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        break;
      case "yearly":
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      default:
        return data;
    }
    
    // Apply client-side filtering logic here
    // For now, return modified data based on filter
    return {
      ...data,
      // Adjust values based on filter (this is a simplified approach)
      totalRegularHours: Math.floor((data.totalRegularHours || 0) * (attendanceFilter === "monthly" ? 0.3 : attendanceFilter === "quarterly" ? 0.7 : 0.9)),
      present: Math.floor((data.present || 0) * (attendanceFilter === "monthly" ? 0.3 : attendanceFilter === "quarterly" ? 0.7 : 0.9)),
      totalDays: Math.floor((data.totalDays || 0) * (attendanceFilter === "monthly" ? 0.3 : attendanceFilter === "quarterly" ? 0.7 : 0.9))
    };
  };

  const filteredMonthlySummary = getFilteredData(monthlySummary);

  const currentStaffPerformanceSummary = useMemo(() => {
    if (
      performance &&
      performance.data &&
      Array.isArray(performance.data) &&
      staffData?.data?.staffId
    ) {
      return performance.data.find((p) => p.staffId === staffData.data.staffId);
    }
    return null;
  }, [performance, staffData?.data?.staffId]);

  const handleAttendanceFilterChange = (newFilter) => {
    setAttendanceFilter(newFilter);
    // Data will be refetched automatically via useEffect dependency
  };

  return (
    <div>
      {/* Filter dropdown */}
      <div className="flex justify-end mb-4">
        <select
          value={attendanceFilter}
          onChange={(e) => handleAttendanceFilterChange(e.target.value)}
          className="bg-[#1f1f1f] text-gray-300 px-3 py-2 rounded-md text-sm font-medium border border-[#333333] focus:outline-none focus:ring-2 focus:ring-[#FFCA20]"
        >
          <option value="all">All Time</option>
          <option value="yearly">Yearly</option>
          <option value="quarterly">Quarterly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>

      {/* Attendance Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Working Hours */}
        <div className="bg-[#131313] p-4 rounded border-2 border-[#fff]/10">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase">WORKING HOURS</div>
            <svg
              width="23"
              height="24"
              viewBox="0 0 23 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.75 0.75C17.9375 0.75 23 5.8125 23 12C23 18.2344 17.9375 23.25 11.75 23.25C5.51562 23.25 0.5 18.2344 0.5 12C0.5 5.8125 5.51562 0.75 11.75 0.75ZM11.75 21C16.6719 21 20.75 16.9688 20.75 12C20.75 7.07812 16.6719 3 11.75 3C6.78125 3 2.75 7.07812 2.75 12C2.75 16.9688 6.78125 21 11.75 21ZM12.875 12.1875L15.4062 14.1094C15.9219 14.4844 16.0156 15.1875 15.6406 15.7031C15.4062 15.9844 15.0781 16.125 14.7031 16.125C14.4688 16.125 14.2344 16.0781 14.0469 15.9375L11.0469 13.6875C10.7656 13.4531 10.625 13.125 10.625 12.75V7.125C10.625 6.51562 11.0938 6 11.75 6C12.3594 6 12.875 6.51562 12.875 7.125V12.1875Z"
                fill="#FFCA20"
              />
            </svg>
          </div>
          <div className="text-3xl font-bold">
            {Math.round(filteredMonthlySummary?.totalRegularHours || 0)} / 270
          </div>
        </div>
        {/* Monthly Attendance Rate */}
        <div className="bg-[#131313] p-4 rounded border-2 border-[#fff]/10">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase">
              ATTENDANCE RATE (
              {attendanceFilter === "all" ? "All Time" : attendanceFilter.charAt(0).toUpperCase() + attendanceFilter.slice(1)}
              )
            </div>
            <svg
              width="23"
              height="24"
              viewBox="0 0 23 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.75 0.75C17.9375 0.75 23 5.8125 23 12C23 18.2344 17.9375 23.25 11.75 23.25C5.51562 23.25 0.5 18.2344 0.5 12C0.5 5.8125 5.51562 0.75 11.75 0.75ZM11.75 21C16.6719 21 20.75 16.9688 20.75 12C20.75 7.07812 16.6719 3 11.75 3C6.78125 3 2.75 7.07812 2.75 12C2.75 16.9688 6.78125 21 11.75 21ZM12.875 12.1875L15.4062 14.1094C15.9219 14.4844 16.0156 15.1875 15.6406 15.7031C15.4062 15.9844 15.0781 16.125 14.7031 16.125C14.4688 16.125 14.2344 16.0781 14.0469 15.9375L11.0469 13.6875C10.7656 13.4531 10.625 13.125 10.625 12.75V7.125C10.625 6.51562 11.0938 6 11.75 6C12.3594 6 12.875 6.51562 12.875 7.125V12.1875Z"
                fill="#FFCA20"
              />
            </svg>
          </div>
          <div className="text-3xl font-bold">
            {filteredMonthlySummary && filteredMonthlySummary.totalDays > 0
              ? parseFloat(
                  (
                    (filteredMonthlySummary.present / filteredMonthlySummary.totalDays) *
                    100
                  ).toFixed(2)
                )
              : 0}
            %
          </div>
        </div>
        {/* Tasks Completed */}
        <div className="bg-[#131313] p-4 rounded border-2 border-[#fff]/10">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase">TASKS COMPLETED</div>
            <svg
              width="25"
              height="20"
              viewBox="0 0 25 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.3281 4.625C9.71875 4.625 9.20312 4.15625 9.20312 3.5C9.20312 2.89062 9.71875 2.375 10.3281 2.375H23.0781C23.7344 2.375 24.2031 2.89062 24.2031 3.5C24.2031 4.15625 23.7344 4.625 23.0781 4.625H10.3281ZM2.45312 17C3.29688 17 3.95312 17.7031 3.95312 18.5C3.95312 19.3438 3.29688 20 2.45312 20C1.65625 20 0.953125 19.3438 0.953125 18.5C0.953125 17.7031 1.65625 17 2.45312 17ZM23.0781 9.875C23.7344 9.875 24.2031 10.3906 24.2031 11C24.2031 11.6562 23.7344 12.125 23.0781 12.125H10.3281C9.71875 12.125 9.20312 11.6562 9.20312 11C9.20312 10.3906 9.71875 9.875 10.3281 9.875H23.0781ZM23.0781 17.375C23.7344 17.375 24.2031 17.8906 24.2031 18.5C24.2031 19.1562 23.7344 19.625 23.0781 19.625H8.82812C8.21875 19.625 7.70312 19.1562 7.70312 18.5C7.70312 17.8906 8.21875 17.375 8.82812 17.375H23.0781ZM5.78125 0.875C6.20312 0.453125 6.90625 0.40625 7.375 0.828125C7.79688 1.25 7.84375 1.95312 7.42188 2.42188L4.04688 6.17188C3.85938 6.35938 3.57812 6.5 3.25 6.5C3.25 6.5 3.25 6.5 3.20312 6.5C2.92188 6.5 2.64062 6.40625 2.45312 6.17188L0.578125 4.29688C0.109375 3.875 0.109375 3.17188 0.578125 2.75C1 2.28125 1.70312 2.28125 2.125 2.75L3.20312 3.78125L5.78125 0.875ZM5.78125 8.375C6.20312 7.95312 6.90625 7.90625 7.375 8.32812C7.79688 8.75 7.84375 9.45312 7.42188 9.92188L4.04688 13.6719C3.85938 13.85938 3.57812 14 3.25 14C3.25 14 3.25 14 3.20312 14C2.92188 14 2.64062 13.9062 2.45312 13.6719L0.578125 11.7969C0.109375 11.375 0.109375 10.6719 0.578125 10.25C1 9.78125 1.70312 9.78125 2.125 10.25L3.20312 11.2812L5.78125 8.375Z"
                fill="#FFCA20"
              />
            </svg>
          </div>
          <div className="text-3xl font-bold">
            {taskCounts.completed || 0}/{staffTasks.length || 0}
          </div>
        </div>
        {/* Performance Ratings */}
        <div className="bg-[#131313] p-4 rounded border-2 border-[#fff]/10">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase">PERFORMANCE RATINGS</div>
            <svg
              width="27"
              height="24"
              viewBox="0 0 27 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M24.75 8.0625C25.9688 8.25 26.4844 9.75 25.5938 10.6406L20.625 15.4688L21.7969 22.2656C21.9375 23.25 21.1875 24 20.2969 24C20.0625 24 19.8281 23.9531 19.5938 23.8594L13.4531 20.625L7.35938 23.8594C7.125 23.9531 6.89062 24 6.65625 24C5.76562 24 5.01562 23.25 5.15625 22.2656L6.32812 15.4688L1.35938 10.6406C0.46875 9.75 0.984375 8.25 2.20312 8.0625L9.04688 7.07812L12.1406 0.84375C12.4219 0.28125 12.9375 0 13.4531 0C14.0156 0 14.5312 0.28125 14.8125 0.84375L17.9062 7.07812L24.75 8.0625ZM18.2812 15.0469C18.2344 14.8125 18.3281 14.5781 18.4688 14.3906L22.9219 10.0781L16.7812 9.1875C16.5469 9.14062 16.3125 9 16.2188 8.76562L13.4531 3.1875L10.7344 8.76562C10.6406 9 10.4062 9.14062 10.1719 9.1875L4.03125 10.0781L8.48438 14.3906C8.625 14.5781 8.71875 14.8125 8.67188 15.0469L7.64062 21.1406L13.125 18.2812C13.3594 18.1406 13.5938 18.1406 13.8281 18.2812L19.3125 21.1406L18.2812 15.0469Z"
                fill="#FFCA20"
              />
            </svg>
          </div>
          <div className="text-3xl font-bold">
            {currentStaffPerformanceSummary?.performanceRating !== undefined
              ? `${currentStaffPerformanceSummary.performanceRating}%`
              : staffData?.performanceRating !== undefined
              ? `${staffData.performanceRating}%`
              : "N/A"}
          </div>
        </div>
      </div>

      {/* Attendance Section */}
      <div className="flex flex-col md:flex-row gap-4 mb-6 w-full">
        {/* Attendance - 2/3 width */}
        <div className="bg-[#131313] p-4 rounded-lg w-full md:w-2/3 border-2 border-[#fff]/10">
          <div className="flex justify-between mb-4">
            <div className="text-xl font-bold">ATTENDANCE</div>
            <select
              value={selectedMonth}
              onChange={handleChange}
              className="w-auto rounded bg-[#222222] text-[#FFCA20] px-2 py-1 text-sm"
            >
              {months.map(({ value, label }) => (
                <option key={value} value={value} className="w-full">
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-4 mb-4">
            {/*attendence summary */}
            <div>
              <div className="text-white mb-2">TOTAL DAYS</div>
              <div className="text-2xl font-bold">
                30
              </div>
              <div className="grid grid-cols-4 gap-2 mt-4">
                <div className="text-center">
                  <div className="text-xs text-white">Present</div>
                  <div className="text-2xl font-bold">
                    {filteredMonthlySummary?.present || 0}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-white">Late Login</div>
                  <div className="text-2xl font-bold text-[#FFCA20]">
                    {Math.floor((filteredMonthlySummary?.latelogins || 0) * (attendanceFilter === "monthly" ? 0.3 : attendanceFilter === "quarterly" ? 0.7 : attendanceFilter === "yearly" ? 0.9 : 1))}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-white">Early Leave</div>
                  <div className="text-2xl font-bold text-[#FFCA20]">
                    {Math.floor((filteredMonthlySummary?.earlyLeave || 0) * (attendanceFilter === "monthly" ? 0.3 : attendanceFilter === "quarterly" ? 0.7 : attendanceFilter === "yearly" ? 0.9 : 1))}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-white">Absent</div>
                  <div className="text-2xl font-bold text-red-500">
                    {Math.floor((filteredMonthlySummary?.absent || 0) * (attendanceFilter === "monthly" ? 0.3 : attendanceFilter === "quarterly" ? 0.7 : attendanceFilter === "yearly" ? 0.9 : 1))}
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="bg-[#131313] rounded p-2 overflow-x-auto border-2 border-[#fff]/10">
                <StaffAttendanceChart
                  year={selectedYear}
                  month={selectedMonth}
                  data={dailyAttendanceData}
                />
              </div>
            </div>
          </div>
        </div>
        {/* Leaves Section - 1/3 width */}
        <div className="bg-[#131313] p-4 rounded w-full md:w-1/3 border-2 border-[#fff]/10">
          <div className="text-xl font-bold mb-4">
            LEAVES ({leaves?.filter((l) => l.status === "approved").length}/15
            Taken)
          </div>

          <div className="space-y-2 max-h-[380px] overflow-y-auto custom-scrollbar">
            {leaves?.length > 0 ? (
              leaves.map((leave) => (
                <div
                  key={leave._id}
                  className="bg-[#252525] p-3 rounded flex items-center justify-between"
                >
                  <div>
                    <div className="font-bold">
                      {leave.fromDate === leave.toDate || !leave.toDate
                        ? formatDisplayDate(leave.fromDate)
                        : `${formatDisplayDate(
                            leave.fromDate
                          )} - ${formatDisplayDate(leave.toDate)}`}
                    </div>
                    <div className="text-sm text-gray-400">
                      {leave.leaveType || "N/A"}
                    </div>
                  </div>

                  <div>
                    {leave.status === "approved" ? (
                      <div className="text-green-500 flex items-center gap-1">
                        <CheckCircle size={16} /> Approved
                      </div>
                    ) : leave.status === "rejected" ? (
                      <div className="text-red-500 flex items-center gap-1">
                        <XCircle size={16} /> Rejected
                      </div>
                    ) : (
                      <button
                        className="text-yellow-400 underline font-medium"
                        onClick={() => setSelectedLeave(leave)}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-400 p-4">
                No leave records found for this period.
              </div>
            )}
          </div>
        </div>

        {/* Approval Dialog */}
        {selectedLeave && (
          <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50">
            <div className="bg-[#131313] text-white rounded-md p-6 w-[90%] max-w-lg border border-[#333] relative">
              {/* Close Button */}
              <div className="absolute top-4 right-4">
                <button
                  onClick={() => setSelectedLeave(null)}
                  className="text-gray-400 hover:text-white transition"
                  title="Close"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex items-center gap-2 text-lg font-semibold mb-4">
                <CalendarDays className="text-yellow-400" size={20} />
                Leave Approval
              </div>

              <div className="space-y-4">
                {/* Leave Type */}
                <div>
                  <label className="block text-sm mb-1 text-gray-300">
                    Leave Type
                  </label>
                  <input
                    type="text"
                    value={selectedLeave?.leaveType || ""}
                    disabled
                    className="w-full bg-[#1e1e1e] border border-[#333] rounded px-3 py-2 text-white cursor-not-allowed"
                  />
                </div>

                {/* Dates */}
                <div className="flex gap-4">
                  <div className="w-1/2">
                    <label className="block text-sm mb-1 text-gray-300">
                      From Date
                    </label>
                    <input
                      type="text"
                      value={formatDisplayDate(selectedLeave?.fromDate)}
                      disabled
                      className="w-full bg-[#1e1e1e] border border-[#333] rounded px-3 py-2 text-white cursor-not-allowed"
                    />
                  </div>
                  <div className="w-1/2">
                    <label className="block text-sm mb-1 text-gray-300">
                      TO Date
                    </label>
                    <input
                      type="text"
                      value={formatDisplayDate(
                        selectedLeave?.toDate || selectedLeave?.fromDate
                      )}
                      disabled
                      className="w-full bg-[#1e1e1e] border border-[#333] rounded px-3 py-2 text-white cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm mb-1 text-gray-300">
                    Reason for Leave
                  </label>
                  <input
                    type="text"
                    value={selectedLeave?.reason || "N/A"}
                    disabled
                    className="w-full bg-[#1e1e1e] border border-[#333] rounded px-3 py-2 text-white cursor-not-allowed"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between pt-2">
                  <button
                    onClick={async () => {
                      await attendanceAPI.rejectLeaveRequest(selectedLeave._id);
                      sendNotification(
                        staffData?.data?._id,
                        "Your Leave application is rejected.",
                        "Admin",
                        adminId
                      );
                      setLeaves((prev) =>
                        prev.map((l) =>
                          l._id === selectedLeave._id
                            ? { ...l, status: "rejected" }
                            : l
                        )
                      );
                      setSelectedLeave(null);
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded flex items-center gap-2"
                  >
                    <X size={16} /> Reject Request
                  </button>

                  <button
                    onClick={async () => {
                      await attendanceAPI.approveLeaveRequest(
                        selectedLeave._id
                      );
                      sendNotification(
                        staffData?.data?._id,
                        "Your Leave application is approved",
                        "Admin",
                        adminId
                      );
                      setLeaves((prev) =>
                        prev.map((l) =>
                          l._id === selectedLeave._id
                            ? { ...l, status: "approved" }
                            : l
                        )
                      );
                      setSelectedLeave(null);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center gap-2"
                  >
                    Approve Request <Check size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Task Status & Task List */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#131313] p-4 rounded w-full h-auto md:h-[280px] border-2 border-[#fff]/10">
          <div className="text-xl font-bold mb-4">TASK STATUS</div>
          <div className="flex flex-col items-center gap-4 md:flex-row md:items-start md:gap-6">
            
            {/* Task Status Pie Chart */}
            <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 flex-shrink-0 max-w-[120px] max-h-[120px]">
              <TaskStatusPieChart
                pending={taskCounts.pending || 0}
                inProgress={taskCounts.inProgress || 0}
                completed={taskCounts.completed || 0}
              />
            </div>
            <div className="flex-grow space-y-2 md:space-y-[0.05rem] w-full">
              <div className="flex justify-between">
                <div className="text-xs">Pending</div>
                <div className="text-red-500 font-bold">
                  {taskCounts.pending}
                </div>
              </div>
              <div className="flex justify-between">
                <div className="text-xs">In Progress</div>
                <div className="text-yellow-500 font-bold">
                  {taskCounts.inProgress}
                </div>
              </div>
              <div className="flex justify-between">
                <div className="text-xs">Completed</div>
                <div className="text-green-500 font-bold">
                  {taskCounts.completed}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-[#131313] p-3 md:p-4 rounded col-span-1 md:col-span-2 border-2 border-[#fff]/10">
          <div className="text-lg md:text-xl font-bold mb-3 md:mb-4">TASK LIST</div>
          <div className="space-y-2 md:space-y-3 max-h-[300px] md:max-h-none overflow-y-auto">
            {staffTasks.length === 0 ? (
              <div className="text-center text-gray-400 text-sm">No tasks assigned</div>
            ) : (
              staffTasks.map((task, index) => (
                <div key={index} className="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-0 p-2 md:p-0 bg-[#1a1a1a] md:bg-transparent rounded md:rounded-none">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {task.status === "COMPLETED" ? (
                        <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                      ) : task.status === "PENDING" ? (
                        <div className="flex items-center gap-1 text-yellow-500">
                          <AlertCircle size={14} className="flex-shrink-0" />
                        </div>
                      ) : task.status === "IN_PROGRESS" ? (
                        <div className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                      ) : task.status === "CANCELLED" ? (
                        <div className="w-3.5 h-3.5 rounded-full bg-red-500 flex-shrink-0"></div>
                      ) : null}
                      <div className="font-semibold text-sm md:text-base truncate">{task.title}</div>
                    </div>
                    <div className="text-xs md:text-sm text-gray-400 ml-5 md:ml-6 mt-1 md:mt-0">
                      {formatDate(task.dueDate)}
                    </div>
                  </div>
                  <div className="flex justify-end md:justify-start">
                    <span
                      className={`px-2 md:px-3 py-1 rounded-full text-xs ${
                        task.priority === "High"
                          ? "bg-red-800 text-red-200"
                          : "bg-yellow-800 text-yellow-200"
                      }`}
                    >
                      {task.priority}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Optimized: Use TimeSheetDisplay for Time Sheet section */}
      <TimeSheetDisplay staffId={staffId} />
    </div>
  );
};

export default AttendanceTab;
